/**
 * AddTenantUserModal.tsx
 *
 * Modal „Dodaj użytkownika" na zakładce Użytkownicy w szczegółach tenanta (panel ITE).
 * Super-admin zaprasza użytkownika-człowieka do tenanta: e-mail, nazwa, username
 * (prefill z części przed @, edytowalny), role (JEDNA LUB WIELE, w kolejności — ADR-0103) i firmy
 * (wiele). Role wybiera się jako linie rodowe (każda rola raz) — serwer przypina kopię właściwą
 * dla każdej wybranej firmy. Dostęp do logowania = zaproszenie mailem (user sam ustawia hasło).
 *
 * Przełącznik „Super admin": pełny dostęp do WSZYSTKICH firm tenanta, jak właściciel — wtedy
 * firm i ról się nie wybiera (pola znikają). Wcześniej zaproszenia nie dało się tak ustawić, a
 * wybór roli „Właściciel" dawał tylko zwykłą rolę w wybranych firmach.
 *
 * POST /api/admin/tenants/:tenantId/users → 201. Po sukcesie: invalidate listy userów,
 * toast, zamknięcie. Błędy domenowe (409 e-mail istnieje itd.) → alert inline + toast.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { MultiSelect, type MultiSelectOption } from '@/components/MultiSelect';
import { CompanyRolesEditor } from '@/components/users/CompanyRolesEditor';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface RoleTemplate {
  id: string;
  name: string;
  isDefaultAdmin: boolean;
}

interface RoleTemplatesResponse {
  roleTemplates: RoleTemplate[];
  dbUnavailable: boolean;
}

interface CompanyRow {
  id: string;
  legalName: string | null;
  shortName: string | null;
}

interface CompaniesResponse {
  companies: CompanyRow[];
  dbUnavailable: boolean;
}

export interface AddTenantUserModalProps {
  tenantId: string;
  open: boolean;
  onClose: () => void;
}

function usernameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  // Username policy: [a-z0-9._-], min 3 — sanitize the derived prefill.
  return local
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '')
    .slice(0, 32);
}

export function AddTenantUserModal({ tenantId, open, onClose }: AddTenantUserModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameDirty, setUsernameDirty] = useState(false);
  const [roleTemplateIds, setRoleTemplateIds] = useState<string[]>([]);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'role-templates'],
    queryFn: async (): Promise<RoleTemplatesResponse> => {
      const res = await api.get<RoleTemplatesResponse>(`/admin/tenants/${tenantId}/role-templates`);
      return res.data;
    },
    enabled: open,
  });

  const companiesQuery = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'companies', 'picker'],
    queryFn: async (): Promise<CompaniesResponse> => {
      const res = await api.get<CompaniesResponse>(
        `/admin/tenants/${tenantId}/companies?limit=100`,
      );
      return res.data;
    },
    enabled: open,
  });

  const companyOptions = useMemo<MultiSelectOption[]>(
    () =>
      (companiesQuery.data?.companies ?? []).map((c) => ({
        value: c.id,
        label: c.shortName ?? c.legalName ?? c.id,
        description: c.shortName && c.legalName ? c.legalName : undefined,
      })),
    [companiesQuery.data],
  );

  // Serwer zwraca jedną pozycję na linię rodową roli (nie kopię na każdą firmę).
  const roleOptions = useMemo(
    () => (rolesQuery.data?.roleTemplates ?? []).map((r) => ({ id: r.id, name: r.name })),
    [rolesQuery.data],
  );
  const roleNameById = useMemo(
    () => new Map(roleOptions.map((r) => [r.id, r.name])),
    [roleOptions],
  );

  function resetForm() {
    setEmail('');
    setDisplayName('');
    setUsername('');
    setUsernameDirty(false);
    setRoleTemplateIds([]);
    setIsSuperUser(false);
    setCompanyIds([]);
    setFormError(null);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/admin/tenants/${tenantId}/users`, {
        email: email.trim(),
        username: username.trim(),
        displayName: displayName.trim() || undefined,
        // Super admin ma dostęp do wszystkiego — firm i ról nie wysyłamy.
        roleTemplateIds: isSuperUser ? [] : roleTemplateIds,
        companyIds: isSuperUser ? [] : companyIds,
        isSuperUser,
        language: 'pl',
      });
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('admin.tenants.detail.users.create.toast.success', { email: email.trim() }),
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'users'] });
      handleClose();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      const msg = errorMessageForCode(code);
      setFormError(msg);
      toast.show({ variant: 'error', title: msg });
    },
  });

  function errorMessageForCode(code: string | undefined): string {
    switch (code) {
      case 'EMAIL_ALREADY_EXISTS_IN_TENANT':
      case 'EMAIL_ALREADY_EXISTS_IN_ADM':
        return t('admin.tenants.detail.users.create.errors.emailExists');
      case 'USERNAME_ALREADY_EXISTS_IN_TENANT':
        return t('admin.tenants.detail.users.create.errors.usernameExists');
      case 'COMPANY_NOT_FOUND':
        return t('admin.tenants.detail.users.create.errors.companyNotFound');
      case 'ROLE_TEMPLATE_NOT_FOUND':
        return t('admin.tenants.detail.users.create.errors.roleNotFound');
      case 'COMPANY_REQUIRED_FOR_ROLE':
        return t('admin.tenants.detail.users.create.errors.roleNeedsCompany');
      case 'ROLE_NOT_IN_COMPANY':
        return t('admin.tenants.detail.users.create.errors.roleNotInCompany');
      case 'TENANT_UPDATING':
        return t('admin.tenants.dbUpdatingError');
      case 'TENANT_DB_UNAVAILABLE':
        return t('admin.tenants.detail.users.create.errors.dbUnavailable');
      case 'VALIDATION_ERROR':
        return t('admin.tenants.detail.users.create.errors.validation');
      default:
        return t('admin.tenants.detail.users.create.errors.generic');
    }
  }

  function handleSubmit() {
    setFormError(null);
    const trimmedEmail = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) {
      setFormError(t('admin.tenants.detail.users.create.validation.emailInvalid'));
      return;
    }
    if (username.trim().length < 3) {
      setFormError(t('admin.tenants.detail.users.create.validation.usernameShort'));
      return;
    }
    if (!isSuperUser && roleTemplateIds.length > 0 && companyIds.length === 0) {
      setFormError(t('admin.tenants.detail.users.create.validation.roleNeedsCompany'));
      return;
    }
    createMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="lg"
      title={t('admin.tenants.detail.users.create.modalTitle')}
      description={t('admin.tenants.detail.users.create.modalDescription')}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={createMutation.isPending}>
            {t('admin.tenants.detail.users.create.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="add-tenant-user-submit"
          >
            {t('admin.tenants.detail.users.create.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
          >
            {formError}
          </div>
        )}

        <Input
          type="email"
          label={t('admin.tenants.detail.users.create.fields.email.label')}
          placeholder={t('admin.tenants.detail.users.create.fields.email.placeholder')}
          value={email}
          required
          onChange={(e) => {
            const v = e.target.value;
            setEmail(v);
            if (!usernameDirty) setUsername(usernameFromEmail(v));
          }}
          data-testid="add-tenant-user-email"
        />

        <Input
          label={t('admin.tenants.detail.users.create.fields.displayName.label')}
          placeholder={t('admin.tenants.detail.users.create.fields.displayName.placeholder')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="add-tenant-user-display-name"
        />

        <Input
          label={t('admin.tenants.detail.users.create.fields.username.label')}
          hint={t('admin.tenants.detail.users.create.fields.username.hint')}
          value={username}
          required
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameDirty(true);
          }}
          data-testid="add-tenant-user-username"
        />

        <Checkbox
          label={t('admin.tenants.detail.users.create.fields.superUser.label')}
          hint={t('admin.tenants.detail.users.create.fields.superUser.hint')}
          checked={isSuperUser}
          onChange={(e) => setIsSuperUser(e.target.checked)}
          data-testid="add-tenant-user-super-user"
        />

        {!isSuperUser && (
          <>
            <FormField
              label={t('admin.tenants.detail.users.create.fields.role.label')}
              hint={t('admin.tenants.detail.users.create.fields.role.hint')}
            >
              <CompanyRolesEditor
                companyId="invite"
                assigned={roleTemplateIds.map((id) => ({ id, name: roleNameById.get(id) ?? '' }))}
                available={roleOptions}
                onChange={setRoleTemplateIds}
                busy={rolesQuery.isLoading}
              />
            </FormField>

            <div className="flex flex-col gap-1">
              <MultiSelect
                label={t('admin.tenants.detail.users.create.fields.companies.label')}
                placeholder={t('admin.tenants.detail.users.create.fields.companies.placeholder')}
                options={companyOptions}
                value={companyIds}
                onChange={setCompanyIds}
                disabled={companiesQuery.isLoading}
                data-testid="add-tenant-user-companies"
              />
              <span className="text-xs text-text-muted">
                {t('admin.tenants.detail.users.create.fields.companies.hint')}
              </span>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
